import { isEqual } from 'lodash';
import { guards } from './typeguards.helpers';

/** Strict equal function. Like === but with special handling for NaN so NaN is equal to NaN */
const strict = (a: unknown, b: unknown) =>
  Number.isNaN(a) ? Number.isNaN(b) : a === b;

/** check if ids are equal */
const byId = <T extends { id: string | number }>(a: T, b: T) =>
  strict(a.id, b.id);

/**
 * Check if two arrays or objects is shallow equal.
 * IE object/array references might differ but first level properties should be the same
 * @param a
 * @param b
 * @returns
 */
function shallowEquals(
  this: { _level_: number } | void,
  a?: unknown,
  b?: unknown,
): boolean {
  const thisLevel = this?._level_ ?? 0;
  // Also handles NaN === NaN
  if (strict(a, b)) {
    return true;
  }

  // Treat null and undefined as the same
  if (!guards.is.defined(a) && !guards.is.defined(b)) {
    return true;
  }

  // Not of the same type
  if (typeof a !== typeof b) {
    return false;
  }

  // Primitive values should be compared directly
  if (guards.is.primitive(a) || guards.is.primitive(b)) {
    return a === b;
  }

  // just bail out if we're not at the first level, since this is shallow equals
  if (thisLevel > 0) {
    return a === b;
  }

  // Narrow to object (non-null) so Object.entries is valid
  if (
    typeof a !== 'object' ||
    a === null ||
    typeof b !== 'object' ||
    b === null
  ) {
    return a === b;
  }

  const entriesA = Object.entries(a);
  const entriesB = Object.entries(b);

  if (entriesA.length !== entriesB.length) {
    return false;
  }

  for (const [keyA, valueA] of entriesA) {
    const [, valueB] = entriesB.find(([key]) => key === keyA) ?? [
      Symbol('NO_KEY'),
      Symbol('NO_VALUE'), // symbols only ever equal themselves
    ];
    if (!shallowEquals.call({ _level_: thisLevel + 1 }, valueA, valueB)) {
      return false;
    }
  }

  return true;
}

/**
 * Type compare utilities to check if properties are equal or not
 * Usage: `compare.equal.shallow(a, b)`
 */
export const compare = {
  equal: {
    /** Strict equal. Like === but with special handling for NaN so NaN is equal to NaN */
    strict,

    /** check that two objects/arrays are either strict equal or have strict equal first level properties  */
    shallow: (a?: unknown, b?: unknown) => shallowEquals(a, b), // Special handling due to the this._level stuff

    /** From {@link isEqual}. Performs a deep comparison between two values to determine if they are
     * equivalent.
     *
     * **Note:** This method supports comparing arrays, array buffers, booleans,
     * date objects, error objects, maps, numbers, `Object` objects, regexes,
     * sets, strings, symbols, and typed arrays. `Object` objects are compared
     * by their own, not inherited, enumerable properties. Functions and DOM
     * nodes are **not** supported. */
    deep: isEqual,

    /** Check that two objects have the same id */
    byId,
  },
} as const;
